import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { BButton, BFormRow } from 'bootstrap-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import draggable from 'vuedraggable'
import { GridLayout, GridItem } from 'vue-grid-layout'
import { Spinner } from '@kwixeo/interface-vue'
import DashboardForm from '../DashboardForm/DashboardForm.vue'
import DashboardDelete from '../DashboardDelete/DashboardDelete.vue'
import WidgetForm from '../WidgetForm/WidgetForm.vue'
import Widget from '../Widget/Widget.vue'
import ForecastList from '../ForecastList/ForecastList.vue'

@Component({
  components: {
    DashboardForm, DashboardDelete, WidgetForm, Widget, ForecastList,
    BButton, BFormRow,
    Spinner,
    draggable,
    GridLayout, GridItem
  },
  computed: {
    ...mapState('dashboard', ['dashboards']),
    ...mapGetters('user', ['can'])
  },
  methods: {
    ...mapActions('dashboard', ['setDashboard', 'loadWidgets'])
  }
})
export default class Dashboard extends Vue {
  dashboards!: KwixeoDashboard.Dashboards
  setDashboard!: (dashboard: KwixeoDashboard.Dashboard) => void
  can!: (key: string, level: number) => boolean
  loadWidgets!: (force?: boolean) => void

  @Prop({ required: true }) value!: KwixeoDashboard.Dashboard
  @Prop({ required: true }) editionMode!: boolean
  @Prop({ required: true }) filters!: KwixeoInterface.GenericObject

  private edit = false
  private deleting = false
  private adding = false
  private loading = true
  private saveTimeout: number|null = null
  private datas: KwixeoDashboard.DashboardDatas = {}
  private manageForecast = false

  private layout: GridLayouts = []
  private initialized = false

  get minWidth (): (widget: KwixeoDashboard.Widget) => number|null {
    return widget => {
      switch (widget.type) {
        case 'number':
        case 'bar':
        case 'diagram':
          return 3
        case 'chronological':
          return 4
        case 'chronological-table':
          return 6
      }
      return null
    }
  }

  get minHeight (): (widget: KwixeoDashboard.Widget) => number|null {
    return widget => {
      switch (widget.type) {
        case 'number':
        case 'chronological-table':
          return 1
        case 'bar':
        case 'diagram':
        case 'chronological':
          return 2
      }
      return null
    }
  }

  @Watch('value', { immediate: true })
  private async watchValue (dashboard: KwixeoDashboard.Dashboard, old: KwixeoDashboard.Dashboard|undefined): Promise<void> {
    this.setLayout()

    if (dashboard.id === old?.id) {
      return
    }

    await this.loadDatas()
  }

  @Watch('filters')
  private async watchFilters (): Promise<void> {
    await this.loadDatas()
  }

  private setLayout (): void {
    this.layout = this.value.widgets.map(widget => ({
      i: String(widget.id),
      w: widget.width,
      h: widget.height,
      x: widget.x,
      y: widget.y,
      widget
    }))
  }

  async loadDatas (): Promise<void> {
    const filters = this.filters
    if (!filters.begin || !filters.end) {
      return
    }
    if ((filters.beginCompare && !filters.endCompare) || (!filters.beginCompare && filters.endCompare)) {
      return
    }

    this.loading = true
    await this.$kwDashboard.axios.get('/dashboard/' + this.value.id + '/datas', {
      params: filters
    }).then(response => {
      this.datas = response.data
      this.loading = false
    })
  }

  private async updateWidget (widget: KwixeoDashboard.Widget): Promise<void> {
    const dashboard = JSON.parse(JSON.stringify(this.value)) as KwixeoDashboard.Dashboard
    const index = dashboard.widgets.findIndex(val => val.id === widget.id)
    if (index !== -1) {
      dashboard.widgets[index] = widget
      await this.dashboardUpdated(dashboard)
    }
  }

  private async dashboardUpdated (dashboard: KwixeoDashboard.Dashboard): Promise<void> {
    this.adding = false
    this.edit = false
    this.deleting = false

    this.setDashboard(dashboard)
    await this.reloadDashboardDatas()
  }

  private async reloadDashboardDatas (): Promise<void> {
    await this.loadWidgets(true)
    await this.loadDatas()
    this.updateWidgetPosition()
  }

  private resizedEvent (id: string, newH: number, newW: number): void {
    const index = this.layout.findIndex(widget => widget.i === id)
    if (index === -1) {
      return
    }

    const widget = this.layout[index]
    widget.widget.width = newW
    widget.widget.height = newH

    this.updateWidgetPosition()
  }

  private movedEvent (id: string, newX: number, newY: number): void {
    const index = this.layout.findIndex(widget => widget.i === id)
    if (index === -1) {
      return
    }

    const widget = this.layout[index]
    widget.widget.x = newX
    widget.widget.y = newY

    this.updateWidgetPosition()
  }

  private updateWidgetPosition (): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    this.saveTimeout = setTimeout(this.savePositions, 2000)
  }

  private async savePositions (): Promise<void> {
    this.saveTimeout = null
    this.$kwDashboard.axios.put('/dashboard/' + this.value.id + '/position', {
      widgets: this.layout.map(widget => ({
        id: widget.i,
        width: widget.w,
        height: widget.h,
        x: widget.x,
        y: widget.y,
      }))
    })
      .then(response => {
        this.setDashboard(response.data)
      })
  }
}
